import { testSessionStatus } from '../lib/testSessionStatus'
import { useMemo } from 'react'
import { useNavigate } from './nav'
import type { TestSession } from '../data/projectTypes'
import type { PullRequest } from '../data/projectTypes'
import { githubPrRunStatus, githubTestRunId, githubTestRunPath, type TestRunOrderItem } from '../lib/githubTestRun'
import type { ProjectPause } from '../lib/projectPause'
import StatusIndicator from './StatusIndicator'
import type { ArtifactEnvironmentDisplayInput } from '../lib/bugEnvironment'
import EnvironmentBadge from './EnvironmentBadge'
import './TestRunTable.css'

interface TestRunTableProps {
  projectId: string
  // A user-facing "test run" is a test SESSION — one round of QA work, holding the exploration(s),
  // journey runs, and bugs it produced. Clicking a row opens that run's detail page.
  sessions: TestSession[]
  /** PRs are joined to persisted sessions by version_id; unmatched PRs render as frontend-only rows. */
  githubPrs?: PullRequest[]
  /** The server's merged order for persisted sessions and unmatched GitHub PR rows. */
  testRunOrder?: TestRunOrderItem[]
  /** The project's pause, if any: replaces a stale live label with the real, reasoned paused state. */
  pause?: ProjectPause | null
}

interface TestRunTableRow {
  id: string
  versionId?: string
  title: string
  startedAt: string
  status: string
  bugCount: number
  journeyCount: number
  environment: ArtifactEnvironmentDisplayInput
  href: string
  testId: string
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(undefined, { month: '2-digit', day: '2-digit', year: 'numeric' })
}

export default function TestRunTable({ projectId, sessions, githubPrs = [], testRunOrder = [], pause = null }: TestRunTableProps) {
  const navigate = useNavigate()
  const rows = useMemo<TestRunTableRow[]>(() => {
    const sessionVersionIds = new Set(sessions.map((session) => session.version_id).filter((id): id is string => !!id))
    const sessionRows = sessions.map((session): TestRunTableRow => {
      const pr = session.version_id ? githubPrs.find((candidate) => candidate.version_id === session.version_id) : undefined
      return {
        id: session.id,
        title: session.title,
        startedAt: session.started_at,
        status: testSessionStatus(session),
        bugCount: session.bugs.length,
        journeyCount: session.journeyRuns.length,
        environment: {
          environment_name: session.environment_name,
          environment_kind: session.environment_kind,
          pr_number: pr?.pr_number ?? session.pr_number,
          pr_repo_full_name: pr?.repo_full_name ?? session.pr_repo_full_name,
        },
        href: `/projects/${projectId}/test-runs/${session.id}`,
        testId: `test-run-row-${session.id}`,
      }
    })
    const githubRows = githubPrs
      .filter((pr) => !sessionVersionIds.has(pr.version_id))
      .map((pr): TestRunTableRow => ({
        id: githubTestRunId(pr.version_id),
        versionId: pr.version_id,
        // Same shape a persisted round's title takes (lib/test-run-title.ts), so a PR whose QA hasn't
        // produced anything yet doesn't read as a different kind of thing from the row it becomes.
        title: pr.pr_title ? `PR #${pr.pr_number}: ${pr.pr_title}` : `PR #${pr.pr_number}`,
        startedAt: pr.updated_at || pr.created_at,
        // 'awaiting-deployment' is not one of the statuses a pause overrides, so a held revision keeps
        // saying what is holding it even on a paused project — the deployment blocks in FRONT of the pause.
        status: githubPrRunStatus(pr),
        bugCount: pr.bugs.length,
        journeyCount: pr.journeys_count,
        environment: {
          environment_name: 'Preview',
          environment_kind: 'preview',
          pr_number: pr.pr_number,
          pr_repo_full_name: pr.repo_full_name,
        },
        href: githubTestRunPath(projectId, pr.version_id),
        testId: `test-run-row-${githubTestRunId(pr.version_id)}`,
      }))

    if (testRunOrder.length === 0) return [...sessionRows, ...githubRows]

    const rowsBySessionId = new Map(sessionRows.map((row) => [row.id, row]))
    const rowsByVersionId = new Map(githubRows.map((row) => [row.versionId, row]))
    const orderedRows: TestRunTableRow[] = []
    const includedIds = new Set<string>()
    for (const item of testRunOrder) {
      const row = item.kind === 'session'
        ? item.session_id ? rowsBySessionId.get(item.session_id) : undefined
        : item.version_id ? rowsByVersionId.get(item.version_id) : undefined
      if (!row || includedIds.has(row.id)) continue
      includedIds.add(row.id)
      orderedRows.push(row)
    }
    return [...orderedRows, ...sessionRows.filter((row) => !includedIds.has(row.id)), ...githubRows.filter((row) => !includedIds.has(row.id))]
  }, [githubPrs, projectId, sessions, testRunOrder])

  return (
    <div className="test-run-table__wrap">
      <table className="test-run-table" data-testid="test-run-table">
        <thead>
          <tr>
            <th className="test-run-table__col-created">Created</th>
            <th>Description</th>
            <th>Environment</th>
            <th>Status</th>
            <th>Bugs</th>
            <th className="test-run-table__col-journeys">Journeys</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr
              key={row.id}
              className="test-run-table__row"
              onClick={() => navigate(row.href)}
              data-testid={row.testId}
              data-analytics-event="test-run-row-open"
            >
              <td className="test-run-table__date test-run-table__col-created">
                {formatDate(row.startedAt)}
              </td>
              <td className="test-run-table__description" title={row.title}>
                {row.title}
              </td>
              <td className="test-run-table__environment" data-testid={`run-environment-${row.id}`}>
                <EnvironmentBadge artifact={row.environment} />
              </td>
              <td>
                <StatusIndicator status={row.status} pause={pause} withIcon />
              </td>
              <td className="test-run-table__bugs" data-testid={`run-bugs-${row.id}`}>
                {row.bugCount}
              </td>
              <td className="test-run-table__col-journeys test-run-table__journeys" data-testid={`run-journeys-${row.id}`}>
                {row.journeyCount}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
